import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  InputAdornment,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Checkbox,
  ListItemText,
  TablePagination,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Divider,
} from '@mui/material';
import {
  Search,
  Visibility,
  Payment,
  Refresh,
  ViewColumn,
  TrendingUp,
} from '@mui/icons-material';
import { getTradeReceivables, getReceivablesSummary } from '../firebase/tradeReceivablesService';
import { addPayment } from '../firebase/creditorService';
import { creditCashAccount } from '../firebase/cashBankService';

const TradeReceivables = ({ currentUser, permissions }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [receivables, setReceivables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [showSettled, setShowSettled] = useState(true); // Show settled by default for historical records
  const [selectedReceivable, setSelectedReceivable] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [summary, setSummary] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Payment form
  const [paymentAmount, setPaymentAmount] = useState('');
  const [payFrom, setPayFrom] = useState('cash_in_hand');
  const [reference, setReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');

  const PAY_FROM_OPTIONS = [
    { value: 'cash_in_hand', label: 'Cash in Hand', method: 'cash' },
    { value: 'cash_at_bank', label: 'Cash at Bank (Bank Transfer)', method: 'bank' },
    { value: 'petty_cash', label: 'Petty Cash', method: 'cash' },
    { value: 'cash_at_bank_cheque', label: 'Cheque (Cash at Bank)', method: 'cheque' },
  ];
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Column visibility
  const [columnAnchor, setColumnAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    index: true,
    customerName: true,
    phone: true,
    email: true,
    category: true,
    invoiceNumber: true,
    totalAmount: true,
    paidAmount: true,
    balance: true,
    dueDate: true,
    status: true,
    paymentType: true,
    actions: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    
    // Load receivables
    const receivablesResult = await getTradeReceivables();
    if (receivablesResult.success) {
      setReceivables(receivablesResult.data);
    }
    
    // Load summary
    const summaryResult = await getReceivablesSummary();
    if (summaryResult.success) {
      setSummary(summaryResult.data);
    }
    
    setLoading(false);
    setRefreshing(false);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const handleViewDetails = (receivable) => {
    setSelectedReceivable(receivable);
    setShowDetailModal(true);
  };

  const handleCloseDetail = () => {
    setShowDetailModal(false);
    setSelectedReceivable(null);
  };

  const handleOpenPaymentDialog = (receivable) => {
    setSelectedReceivable(receivable);
    setPaymentAmount(receivable.balanceAmount.toString());
    setPayFrom('cash_in_hand');
    setReference('');
    setPaymentNotes('');
    setPaymentDialog(true);
  };

  const handleRecordPayment = async () => {
    if (submitting) return;
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      showSnackbar('Please enter a valid payment amount', 'error');
      return;
    }
    if (parseFloat(paymentAmount) > selectedReceivable.balanceAmount) {
      showSnackbar('Payment amount cannot exceed balance amount', 'error');
      return;
    }
    const selectedOption = PAY_FROM_OPTIONS.find(o => o.value === payFrom) || PAY_FROM_OPTIONS[0];
    const accountType = selectedOption.value.replace('_cheque', '');
    const paymentMethod = selectedOption.method;
    const paymentData = {
      amount: parseFloat(paymentAmount),
      date: new Date().toISOString().split('T')[0],
      notes: paymentNotes,
      selectedBills: selectedReceivable.bills || [],
      paymentMethod,
    };
    setSubmitting(true);
    const result = await addPayment(selectedReceivable.id, paymentData);
    setSubmitting(false);
    if (result.success) {
      creditCashAccount(accountType, parseFloat(paymentAmount), selectedReceivable.id, 'trade_receivable', `Customer Payment - ${selectedReceivable.customerName}`, paymentMethod, null, currentUser)
        .catch(err => console.error('Cash ledger credit failed:', err));
      showSnackbar('Payment recorded successfully', 'success');
      setPaymentDialog(false);
      loadData();
    } else {
      showSnackbar(result.error || 'Failed to record payment', 'error');
    }
  };

  const handleColumnMenuOpen = (event) => {
    setColumnAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnAnchor(null);
  };

  const toggleColumn = (column) => {
    setVisibleColumns({
      ...visibleColumns,
      [column]: !visibleColumns[column],
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter receivables
  const filteredReceivables = receivables.filter(receivable => {
    const matchesSearch = 
      receivable.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (receivable.customerPhone && receivable.customerPhone.includes(searchTerm)) ||
      (receivable.invoiceNumber && receivable.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'All' || receivable.status === statusFilter;
    
    const matchesCategory = categoryFilter === 'All' || receivable.category === categoryFilter;
    
    // Filter out settled receivables if showSettled is false
    const matchesSettledFilter = showSettled || receivable.status !== 'paid';
    
    return matchesSearch && matchesStatus && matchesCategory && matchesSettledFilter;
  });

  // Paginate receivables
  const paginatedReceivables = filteredReceivables.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculate totals - only from Active receivables to show actual receivables
  const totals = filteredReceivables.reduce(
    (acc, receivable) => ({
      totalAmount: acc.totalAmount + (receivable.totalAmount || 0),
      paidAmount: acc.paidAmount + (receivable.paidAmount || 0),
      balance: acc.balance + (receivable.balanceAmount || 0),
    }),
    { totalAmount: 0, paidAmount: 0, balance: 0 }
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'partially_paid':
        return 'info';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'paid':
        return 'Paid';
      case 'pending':
        return 'Pending';
      case 'partially_paid':
        return 'Partially Paid';
      case 'overdue':
        return 'Overdue';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Trade Receivables Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track and manage customer receivables and credit payments
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Outstanding
                </Typography>
                <Typography variant="h5" fontWeight={700} color="primary">
                  LKR {summary.totalOutstanding.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Overdue Amount
                </Typography>
                <Typography variant="h5" fontWeight={700} color="error.main">
                  LKR {summary.totalOverdue.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Due This Week
                </Typography>
                <Typography variant="h5" fontWeight={700} color="warning.main">
                  LKR {summary.dueThisWeek.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Pending Count
                </Typography>
                <Typography variant="h5" fontWeight={700} color="success.main">
                  {summary.pendingCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight={600}>
              All Receivables ({filteredReceivables.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ViewColumn />}
                onClick={handleColumnMenuOpen}
              >
                Columns
              </Button>
              <Button
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={20} /> : <Refresh />}
                onClick={handleRefresh}
                disabled={refreshing}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by customer name, phone, or invoice..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status Filter"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="All">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="partially_paid">Partially Paid</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="All">All Categories</MenuItem>
                  <MenuItem value="Koko">Koko</MenuItem>
                  <MenuItem value="MintPay">MintPay</MenuItem>
                  <MenuItem value="Credit">Credit</MenuItem>
                  <MenuItem value="COD">COD</MenuItem>
                  <MenuItem value="Regular">Regular</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pl: 2 }}>
                <FormControl component="fieldset">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={showSettled}
                      onChange={(e) => setShowSettled(e.target.checked)}
                    />
                    <Typography variant="body2">
                      Show Paid
                    </Typography>
                  </Box>
                </FormControl>
              </Box>
            </Grid>
          </Grid>

          {/* Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  {visibleColumns.index && <TableCell><strong>#</strong></TableCell>}
                  {visibleColumns.customerName && <TableCell><strong>Customer Name</strong></TableCell>}
                  {visibleColumns.phone && <TableCell><strong>Phone</strong></TableCell>}
                  {visibleColumns.email && <TableCell><strong>Email</strong></TableCell>}
                  {visibleColumns.category && <TableCell><strong>Category</strong></TableCell>}
                  {visibleColumns.invoiceNumber && <TableCell><strong>Invoice #</strong></TableCell>}
                  {visibleColumns.totalAmount && <TableCell align="right"><strong>Total Amount (LKR)</strong></TableCell>}
                  {visibleColumns.paidAmount && <TableCell align="right"><strong>Paid Amount (LKR)</strong></TableCell>}
                  {visibleColumns.balance && <TableCell align="right"><strong>Balance (LKR)</strong></TableCell>}
                  {visibleColumns.dueDate && <TableCell><strong>Due Date</strong></TableCell>}
                  {visibleColumns.status && <TableCell><strong>Status</strong></TableCell>}
                  {visibleColumns.paymentType && <TableCell><strong>Type</strong></TableCell>}
                  {visibleColumns.actions && <TableCell align="center"><strong>Actions</strong></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedReceivables.map((receivable, index) => {
                  // Check if payment is overdue
                  const isOverdue = receivable.dueDate && 
                    new Date(receivable.dueDate.toDate()) < new Date() && 
                    (receivable.status === 'pending' || receivable.status === 'partially_paid');
                  
                  return (
                  <TableRow key={receivable.id} hover sx={{ 
                    backgroundColor: isOverdue ? 'error.lighter' : 'inherit',
                    '&:hover': { backgroundColor: isOverdue ? 'error.light' : 'action.hover' }
                  }}>
                    {visibleColumns.index && (
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    )}
                    {visibleColumns.customerName && (
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {receivable.customerName}
                        </Typography>
                        {receivable.customerType && (
                          <Typography variant="caption" color="text.secondary">
                            {receivable.customerType === 'retail' ? 'Retail' : 'Wholesale'}
                          </Typography>
                        )}
                        {receivable.category && (
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={receivable.category}
                              size="small"
                              color={(receivable.category === 'Koko' || receivable.category === 'MintPay') ? 'warning' : 'default'}
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </Box>
                        )}
                        {isOverdue && (
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={`Due: ${receivable.dueDate?.toDate().toLocaleDateString()}`}
                              size="small"
                              color="error"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </Box>
                        )}
                        {(receivable.category === 'Koko' || receivable.category === 'MintPay') && receivable.dueDate && !isOverdue && receivable.status !== 'paid' && (
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={`Due: ${receivable.dueDate?.toDate().toLocaleDateString()}`}
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </Box>
                        )}
                      </TableCell>
                    )}
                    {visibleColumns.phone && (
                      <TableCell>
                        {receivable.customerPhone ? (
                          <Box
                            component="a"
                            href={`tel:${receivable.customerPhone}`}
                            sx={{
                              color: 'info.main',
                              textDecoration: 'none',
                              fontWeight: 500,
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {receivable.customerPhone}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    {visibleColumns.email && (
                      <TableCell>
                        {receivable.customerEmail ? (
                          <Typography variant="body2" color="text.secondary">
                            {receivable.customerEmail}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    {visibleColumns.category && (
                      <TableCell>
                        {receivable.category && (
                          <Chip
                            label={receivable.category}
                            size="small"
                            color={(receivable.category === 'Koko' || receivable.category === 'MintPay') ? 'warning' : 'default'}
                          />
                        )}
                      </TableCell>
                    )}
                    {visibleColumns.invoiceNumber && (
                      <TableCell>
                        <Typography variant="body2">
                          {receivable.invoiceNumber || '-'}
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.totalAmount && (
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {(receivable.totalAmount || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.paidAmount && (
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main" fontWeight={600}>
                          {(receivable.paidAmount || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.balance && (
                      <TableCell align="right">
                        <Typography 
                          variant="body2" 
                          fontWeight={700}
                          color={receivable.balanceAmount > 0 ? 'error.main' : 'success.main'}
                        >
                          {(receivable.balanceAmount || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.dueDate && (
                      <TableCell>
                        {receivable.dueDate ? (
                          <Typography variant="body2">
                            {receivable.dueDate.toDate().toLocaleDateString()}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    {visibleColumns.status && (
                      <TableCell>
                        <Chip
                          label={getStatusLabel(receivable.status)}
                          size="small"
                          color={getStatusColor(receivable.status)}
                        />
                      </TableCell>
                    )}
                    {visibleColumns.paymentType && (
                      <TableCell>
                        <Chip
                          label={receivable.category || receivable.paymentType || 'Credit'}
                          size="small"
                          color={(receivable.category === 'Koko' || receivable.category === 'MintPay') ? 'warning' : receivable.category === 'COD' ? 'info' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                    )}
                    {visibleColumns.actions && (
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDetails(receivable)}
                          title="View Details & Payment History"
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                        {receivable.status !== 'paid' && (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleOpenPaymentDialog(receivable)}
                            title="Add Payment"
                          >
                            <Payment fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                  );
                })}
                {paginatedReceivables.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {searchTerm || statusFilter !== 'All' || categoryFilter !== 'All'
                          ? 'No receivables found matching your filters'
                          : 'No receivables yet. Credit/COD sales will appear here.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                
                {/* Totals Row */}
                {filteredReceivables.length > 0 && (
                  <TableRow sx={{ 
                    backgroundColor: 'action.selected',
                    borderTop: '2px solid',
                    borderTopColor: 'divider',
                    fontWeight: 700,
                  }}>
                    {visibleColumns.index && <TableCell />}
                    {visibleColumns.customerName && (
                      <TableCell>
                        <Typography variant="body2" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Totals
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.phone && <TableCell />}
                    {visibleColumns.email && <TableCell />}
                    {visibleColumns.category && <TableCell />}
                    {visibleColumns.invoiceNumber && <TableCell />}
                    {visibleColumns.totalAmount && (
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700}>
                          {totals.totalAmount.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.paidAmount && (
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} color="success.main">
                          {totals.paidAmount.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.balance && (
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} color="error.main">
                          {totals.balance.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.dueDate && <TableCell />}
                    {visibleColumns.status && <TableCell />}
                    {visibleColumns.paymentType && <TableCell />}
                    {visibleColumns.actions && <TableCell />}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredReceivables.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>

      {/* Column Visibility Menu */}
      <Menu
        anchorEl={columnAnchor}
        open={Boolean(columnAnchor)}
        onClose={handleColumnMenuClose}
      >
        {Object.keys(visibleColumns).map((column) => (
          <MenuItem key={column} onClick={() => toggleColumn(column)}>
            <Checkbox checked={visibleColumns[column]} />
            <ListItemText primary={column.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} />
          </MenuItem>
        ))}
      </Menu>

      {/* Receivable Detail Modal */}
      <Dialog open={showDetailModal} onClose={handleCloseDetail} maxWidth="md" fullWidth>
        <DialogTitle>Receivable Details</DialogTitle>
        <DialogContent>
          {selectedReceivable && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Customer</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedReceivable.customerName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">
                    {selectedReceivable.customerPhone ? (
                      <Box
                        component="a"
                        href={`tel:${selectedReceivable.customerPhone}`}
                        sx={{ color: 'info.main', textDecoration: 'none' }}
                      >
                        {selectedReceivable.customerPhone}
                      </Box>
                    ) : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedReceivable.customerEmail || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Category</Typography>
                  <Chip 
                    label={selectedReceivable.category || 'Regular'} 
                    size="small"
                    color={(selectedReceivable.category === 'Koko' || selectedReceivable.category === 'MintPay') ? 'warning' : 'default'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Invoice Number</Typography>
                  <Typography variant="body1">{selectedReceivable.invoiceNumber}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Payment Type</Typography>
                  <Chip 
                    label={selectedReceivable.paymentType === 'credit' ? 'Credit' : 'COD'} 
                    size="small"
                    color={selectedReceivable.paymentType === 'credit' ? 'warning' : 'info'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    LKR {selectedReceivable.totalAmount.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Balance</Typography>
                  <Typography variant="body1" fontWeight={600} color="error.main">
                    LKR {selectedReceivable.balanceAmount.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Invoice Date</Typography>
                  <Typography variant="body1">
                    {selectedReceivable.invoiceDate?.toDate().toLocaleDateString() || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Due Date</Typography>
                  <Typography variant="body1">
                    {selectedReceivable.dueDate?.toDate().toLocaleDateString() || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Credit Terms</Typography>
                  <Typography variant="body1">{selectedReceivable.creditTerms}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={getStatusLabel(selectedReceivable.status)} 
                    color={getStatusColor(selectedReceivable.status)}
                    size="small"
                  />
                </Grid>
              </Grid>

              {selectedReceivable.payments && selectedReceivable.payments.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Payment History</Typography>
                  <List>
                    {selectedReceivable.payments.map((payment, index) => (
                      <React.Fragment key={payment.id}>
                        <ListItem>
                          <ListItemText
                            primary={`LKR ${payment.amount.toLocaleString()}`}
                            secondary={`${payment.date?.toDate ? payment.date.toDate().toLocaleDateString() : payment.date} - ${payment.paymentMethod} - ${payment.reference || 'No reference'}`}
                          />
                        </ListItem>
                        {index < selectedReceivable.payments.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          {selectedReceivable && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Alert severity="info">
                Balance Amount: LKR {selectedReceivable.balanceAmount.toLocaleString()}
              </Alert>

              <TextField
                label="Payment Amount"
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                required
                fullWidth
              />

              <TextField
                label="Pay From"
                select
                value={payFrom}
                onChange={(e) => setPayFrom(e.target.value)}
                fullWidth
              >
                {PAY_FROM_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>

              <TextField
                label="Reference Number"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Receipt number, transaction ID, etc."
                fullWidth
              />

              <TextField
                label="Notes"
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                multiline
                rows={2}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleRecordPayment} variant="contained" disabled={submitting}>
            {submitting ? 'Recording...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TradeReceivables;
